import React, { useState } from "react";
import AppLayout from "../../../layouts/AppLayout";
import { Button, DatePicker, Dropdown, Input, Select, TimePicker } from "antd";
import IconButton from "../../../components/ui/IconButton";
import BottomDropdownIcon from "../../../assets/icons/BottomDropdownIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import useTable from "../../../hooks/useTable";
import { GetSupplierPaymentHistoryParams } from "../../../types/statements";
import dayjs, { Dayjs } from "dayjs";
import { dbDateFormat } from "../../../constants/constants";
import { disableFutureDates } from "../../../lib/time";
import UploadIcon from "../../../assets/icons/UploadIcon";
import CustomTable from "../../../components/ui/CustomTable";
import { addKeyInArray } from "../../../utils/utils";
import { ColumnsType } from "antd/es/table";
import moment from "moment";
import { Link } from "react-router-dom";
import BackIcon from "../../../assets/icons/BackIcon";

type TimeFormat = {
  formTime?: string;
  toTime?: string;
};

export default function Reconcile() {
  const loading = false;
  const [time, setTime] = useState<TimeFormat>();
  const [openDropdown, setOpenDropdown] = useState(false);
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable<GetSupplierPaymentHistoryParams>();

  const handleStatementCancel = () => {
    handleFilter("startDate", undefined);
    handleFilter("endDate", undefined);
    setTime(undefined);
  };

  const DateTimeSelector = (
    <div className="p-6 border rounded-lg shadow-md max-w-md mx-auto bg-white clear-button-hide">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-600 mb-1">From Date</label>
          <DatePicker
            value={
              filter?.startDate ? dayjs(filter?.startDate, dbDateFormat) : null
            }
            onChange={(e: Dayjs | null) =>
              handleFilter("startDate", dayjs(e).format(dbDateFormat))
            }
            className="w-full"
            size="large"
            disabledDate={disableFutureDates}
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">Time</label>
          <TimePicker
            value={time?.formTime ? dayjs(time?.formTime, "HH:mm:ss") : null}
            onChange={(e: Dayjs | null) =>
              setTime((prev) => ({
                ...prev,
                formTime: dayjs(e).format("HH:mm:ss"),
              }))
            }
            className="w-full"
            format="hh:mm A"
            size="large"
            use12Hours
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">To Date</label>
          <DatePicker
            value={
              filter?.endDate ? dayjs(filter?.endDate, dbDateFormat) : null
            }
            onChange={(e: Dayjs | null) =>
              handleFilter("endDate", dayjs(e).format(dbDateFormat))
            }
            className="w-full"
            size="large"
            disabledDate={disableFutureDates}
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">Time</label>
          <TimePicker
            value={time?.toTime ? dayjs(time?.toTime, "HH:mm:ss") : null}
            onChange={(e: Dayjs | null) => {
              setTime((prev) => ({
                ...prev,
                toTime: dayjs(e).format("HH:mm:ss"),
              }));
            }}
            className="w-full"
            format="hh:mm A"
            size="large"
            use12Hours
          />
        </div>
      </div>
      <div className="flex justify-end mt-6">
        <Button className="text-gray-600" onClick={handleStatementCancel}>
          Cancel
        </Button>
        {/* <Button
                  type="primary"
                  className="!text-primary !bg-[#147AFF33]"
                  onClick={() => setOpenDropdown(false)}
                >
                  View Statement
                </Button> */}
      </div>
    </div>
  );

  const columns: ColumnsType<DataType> = [
    {
      title: <div className="text-nowrap w-[150px]">Uploaded by</div>,
      key: "uploadedBy",
      render: (val) => (
        <div className="flex items-center gap-2">
          <div className="w-12 h-12 rounded-full">
            <img
              src={val?.merchant?.profile?.logoImage}
              alt="Dealer"
              className="rounded-full w-full h-full object-cover"
            />
          </div>
          <div>
            <div className="flex-1">{val?.merchant?.name}</div>
            <div className="flex-1 text-slate-500">Admin</div>
          </div>
        </div>
      ),
    },
    {
      title: <div className="text-nowrap">Uploaded Date</div>,
      key: "uploadedDate",
      dataIndex: "createdAt",
      align: "center",
      render: (date) => (
        <span className="text-nowrap">
          {date ? moment(date).format("DD/MM/YYYY") : "-"}
        </span>
      ),
    },
    {
      title: <div className="text-nowrap">Uploaded Time</div>,
      key: "uploadedTime",
      dataIndex: "createdAt",
      align: "center",
      render: (date) => (
        <span className="text-nowrap">
          {date ? moment(date).format("hh:mm A") : "-"}
        </span>
      ),
    },
    {
      title: <div className="text-nowrap">Uploaded File</div>,
      key: "uploadedFile",
      dataIndex: "uploadedFile",
      align: "center",
      render: (val) => val || "N/A",
    },
    {
      title: <div className="text-nowrap">Verification Status</div>,
      key: "verificationStatus",
      dataIndex: "verificationStatus",
      align: "center",
      render: (val) => val || "N/A",
    },
    {
      title: <div className="text-nowrap">Matched (%)</div>,
      key: "matchedPercent",
      dataIndex: "matchedPercent",
      align: "center",
      render: (val) => val || "N/A",
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: () => (
        <Link to={`/admin/reconcile/reconcile-details/${1}`}>
          <Button className="text-gray-600">Details</Button>
        </Link>
      ),
    },
  ];

  return (
    <>
      <AppLayout title="Reconcile">
        <div className="self-stretch justify-start !text-[#2D3643] text-4xl font-medium mb-5">
          Reconcile
        </div>
        <div className="flex justify-between items-center ">
          <div className="flex justify-start items-center gap-3">
            <Dropdown
              overlay={DateTimeSelector}
              trigger={["click"]}
              open={openDropdown}
              onOpenChange={setOpenDropdown}
            >
              <IconButton
                className="flex items-center text-[#7a8799]"
                // nextIcon={<BottomDropdownIcon color="#7a8799" />}
              >
                Onboard Date
              </IconButton>
            </Dropdown>

            <Select
              placeholder="Status"
              suffixIcon={<BottomDropdownIcon color="#7a8799" />}
              // onChange={(val) => handleFilter("spayStatus", val)}
              className="min-w-[100px]"
              allowClear
            />
          </div>

          <div className="flex justify-start items-center gap-3">
            <Input
              placeholder="Search dealer’s name or code"
              className="text-[#AFBACA] !w-[280px]"
              prefix={<SearchIcon />}
              //   onChange={handleSearch}
              allowClear
            />
            <Button
              className="flex items-center justify-center gap-1 !text-primary bg-[#147AFF33]"
              icon={<UploadIcon />}
            >
              Upload Statement
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <CustomTable
            columns={columns}
            className="text-header overflow-auto mt-4"
            loading={loading}
            handleLimit={handleLimit}
            handlePagination={handlePagination}
            limit={limit}
            dataList={addKeyInArray(data)}
            page={page}
            bordered={false}
            total={120}
            hideSpace
            paginationClassnames="dark:bg-primaryBackground bg-white dark:border-others-body border-others-borderLight py-4 px-4 relative"
            // showTotalPage
          />
        </div>
      </AppLayout>
    </>
  );
}

interface DataType {
  key: string;
  merchant: {
    name: string;
    profile: {
      logoImage: string;
    };
  };
  createdAt: string;
  uploadedFile: string;
  walletNumber: string;
  matchedPercent: string;
}

const data: DataType[] = [];
